import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  Calendar, Mail, FileText, Clock, Send, Download, 
  Settings, CheckCircle2, XCircle, RefreshCw, Loader2,
  BarChart3, Users, Heart, Shield, DollarSign, Sparkles,
  Eye, Play, History, TrendingUp, AlertTriangle, Zap
} from 'lucide-react';
import { 
  PageHeader, MetricCard, ModernCard, SectionHeader,
  modernTabsClass, modernTabTriggerClass 
} from '@/components/ModernUI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REPORT_SECTIONS = [
  { id: 'executive_summary', name: 'Executive Summary', icon: BarChart3, description: 'High-level KPIs and trends' },
  { id: 'clinical_quality', name: 'Clinical Quality', icon: Heart, description: 'Pain control, hospitalizations, symptoms' },
  { id: 'workforce', name: 'Workforce & Capacity', icon: Users, description: 'Staff metrics, burnout risk, capacity' },
  { id: 'compliance', name: 'Compliance Status', icon: Shield, description: 'Regulatory metrics, audit readiness' },
  { id: 'financial', name: 'Financial Overview', icon: DollarSign, description: 'Revenue, costs, ADR rates' },
  { id: 'pips', name: 'PIP Progress', icon: TrendingUp, description: 'Active PIPs and milestones' },
  { id: 'alerts', name: 'Alerts & Actions', icon: AlertTriangle, description: 'Items requiring attention' },
  { id: 'ai_insights', name: 'AI Insights', icon: Sparkles, description: 'AI-generated recommendations' },
];

const DAYS_OF_WEEK = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
];

export default function WeeklyReports() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('configure');
  const [reportHistory, setReportHistory] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  
  // Report Configuration State
  const [config, setConfig] = useState({
    enabled: true,
    schedule_day: '1', // Monday
    schedule_time: '07:00',
    recipients: [],
    sections: ['executive_summary', 'clinical_quality', 'workforce', 'compliance'],
    include_ai_insights: true,
    format: 'pdf',
    subject_prefix: '[CareLens Weekly]',
    new_recipient: ''
  });

  useEffect(() => {
    fetchReportConfig();
    fetchReportHistory();
  }, [token]);

  const fetchReportConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/reports/weekly/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.config) {
          setConfig(prev => ({ ...prev, ...data.config }));
        }
      }
    } catch (error) {
      console.error('Error fetching config:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReportHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/reports/weekly/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setReportHistory(data.reports || []);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/reports/weekly/config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(config)
      });
      if (res.ok) {
        toast.success('Report configuration saved');
      } else {
        toast.error('Failed to save configuration');
      }
    } catch (error) {
      toast.error('Error saving configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateNow = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/reports/weekly/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ sections: config.sections, include_ai: config.include_ai_insights })
      });
      if (res.ok) {
        const data = await res.json();
        setPreviewData(data);
        toast.success('Report generated successfully');
        setActiveTab('preview');
        fetchReportHistory();
      } else {
        toast.error('Failed to generate report');
      }
    } catch (error) {
      toast.error('Error generating report');
    } finally {
      setGenerating(false);
    }
  };

  const [sending, setSending] = useState(false);
  const [htmlPreview, setHtmlPreview] = useState(null);

  const handleSendNow = async () => {
    if (config.recipients.length === 0) {
      toast.error('Please add at least one recipient');
      return;
    }
    
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/reports/weekly/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          recipients: config.recipients,
          subject: `${config.subject_prefix} Weekly Report - ${new Date().toLocaleDateString()}`
        })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.email_status === 'pending') {
          toast.warning('Report generated but email service not fully configured');
        } else {
          toast.success(`Report sent to ${config.recipients.length} recipients`);
        }
        fetchReportHistory();
      } else {
        toast.error('Failed to send report');
      }
    } catch (error) {
      toast.error('Error sending report');
    } finally {
      setSending(false);
    }
  };

  const handlePreviewEmail = async () => {
    try {
      const res = await fetch(`${API_URL}/api/reports/weekly/preview/new`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHtmlPreview(data.html);
        setActiveTab('email-preview');
      }
    } catch (error) {
      console.error('Error fetching email preview:', error);
    }
  };

  const handleAddRecipient = () => {
    const email = config.new_recipient.trim();
    if (email && email.includes('@') && !config.recipients.includes(email)) {
      setConfig(prev => ({
        ...prev,
        recipients: [...prev.recipients, email],
        new_recipient: ''
      }));
    }
  };

  const handleRemoveRecipient = (email) => {
    setConfig(prev => ({
      ...prev,
      recipients: prev.recipients.filter(r => r !== email)
    }));
  };

  const toggleSection = (sectionId) => {
    setConfig(prev => ({
      ...prev,
      sections: prev.sections.includes(sectionId)
        ? prev.sections.filter(s => s !== sectionId)
        : [...prev.sections, sectionId]
    }));
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 w-full rounded-2xl" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const nextScheduledDate = () => {
    const now = new Date();
    const targetDay = parseInt(config.schedule_day);
    const [hours, minutes] = config.schedule_time.split(':').map(Number);
    
    const daysUntilTarget = (targetDay - now.getDay() + 7) % 7 || 7;
    const nextDate = new Date(now);
    nextDate.setDate(now.getDate() + daysUntilTarget);
    nextDate.setHours(hours, minutes, 0, 0);
    
    return nextDate.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="weekly-reports-page">
      {/* Modern Gradient Header */}
      <PageHeader
        icon={Calendar}
        title="Automated Weekly Reports"
        subtitle="Configure and manage automated report delivery"
        gradient="from-[#00b0ab] via-[#009590] to-[#48494b]"
      >
        <Button 
          variant="secondary"
          className="bg-white/20 hover:bg-white/30 text-white border-0 backdrop-blur-sm"
          onClick={handleGenerateNow}
          disabled={generating}
        >
          {generating ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Play className="h-4 w-4 mr-2" />
          )}
          Generate Now
        </Button>
        <Button 
          className="bg-white text-[#00b0ab] hover:bg-white/90"
          onClick={handleSendNow}
          disabled={config.recipients.length === 0 || sending}
        >
          {sending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Send className="h-4 w-4 mr-2" />
          )}
          Send Report
        </Button>
      </PageHeader>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <MetricCard
          title="Report Status"
          value={config.enabled ? 'Active' : 'Paused'}
          icon={config.enabled ? CheckCircle2 : XCircle}
          gradient={config.enabled ? 'emerald' : 'rose'}
          subtitle={config.enabled ? 'Automated delivery on' : 'Automated delivery off'}
        />
        <MetricCard
          title="Next Scheduled"
          value={DAYS_OF_WEEK.find(d => d.value === config.schedule_day)?.label || 'Monday'}
          icon={Clock}
          gradient="blue"
          subtitle={config.schedule_time}
        />
        <MetricCard
          title="Recipients"
          value={config.recipients.length}
          icon={Mail}
          gradient="amber"
          subtitle="Email addresses"
        />
        <MetricCard
          title="Report Sections"
          value={config.sections.length}
          icon={FileText}
          gradient="indigo"
          subtitle="Included in report"
        />
      </div>

      {/* Next Report Banner */}
      {config.enabled && (
        <Card className="border-violet-200 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-[#008a86]/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-violet-500 rounded-xl">
                <Calendar className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-violet-800 dark:text-violet-200">Next Automated Report</h3>
                <p className="text-sm text-violet-700 dark:text-violet-300">
                  Scheduled for {nextScheduledDate()}
                </p>
              </div>
              <Badge className="bg-violet-500 text-white">{config.recipients.length} recipients</Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={modernTabsClass}>
          <TabsTrigger value="configure" className={modernTabTriggerClass}>
            <Settings className="h-4 w-4 mr-2" /> Configure
          </TabsTrigger>
          <TabsTrigger value="sections" className={modernTabTriggerClass}>
            <FileText className="h-4 w-4 mr-2" /> Report Sections
          </TabsTrigger>
          <TabsTrigger value="preview" className={modernTabTriggerClass}>
            <Eye className="h-4 w-4 mr-2" /> Preview
          </TabsTrigger>
          <TabsTrigger value="history" className={modernTabTriggerClass}>
            <History className="h-4 w-4 mr-2" /> History
          </TabsTrigger>
          <TabsTrigger value="email-preview" className={modernTabTriggerClass}>
            <Mail className="h-4 w-4 mr-2" /> Email Preview
          </TabsTrigger>
        </TabsList>

        {/* Configure Tab */}
        <TabsContent value="configure" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Schedule Settings */}
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={Clock} title="Schedule Settings" />
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <div className="space-y-1">
                    <Label className="text-base font-medium">Enable Automated Reports</Label>
                    <p className="text-sm text-muted-foreground">Send reports on schedule</p>
                  </div>
                  <Switch
                    checked={config.enabled}
                    onCheckedChange={(checked) => setConfig(prev => ({ ...prev, enabled: checked }))}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Day of Week</Label>
                    <Select
                      value={config.schedule_day}
                      onValueChange={(value) => setConfig(prev => ({ ...prev, schedule_day: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DAYS_OF_WEEK.map(day => (
                          <SelectItem key={day.value} value={day.value}>{day.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Time</Label>
                    <Input
                      type="time"
                      value={config.schedule_time}
                      onChange={(e) => setConfig(prev => ({ ...prev, schedule_time: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Report Format</Label>
                  <Select
                    value={config.format}
                    onValueChange={(value) => setConfig(prev => ({ ...prev, format: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pdf">PDF Document</SelectItem>
                      <SelectItem value="word">Word Document</SelectItem>
                      <SelectItem value="html">HTML Email</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-violet-50 to-indigo-50 dark:from-violet-900/20 dark:to-indigo-900/20 rounded-lg">
                  <Sparkles className="h-5 w-5 text-violet-500" />
                  <div className="flex-1">
                    <Label className="text-sm font-medium">Include AI Insights</Label>
                    <p className="text-xs text-muted-foreground">GPT-powered analysis</p>
                  </div>
                  <Switch
                    checked={config.include_ai_insights}
                    onCheckedChange={(checked) => setConfig(prev => ({ ...prev, include_ai_insights: checked }))}
                  />
                </div>
              </CardContent>
            </ModernCard>

            {/* Recipients */}
            <ModernCard>
              <CardHeader>
                <SectionHeader icon={Mail} title="Email Recipients" subtitle="Who receives the report" />
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Enter email address"
                    value={config.new_recipient}
                    onChange={(e) => setConfig(prev => ({ ...prev, new_recipient: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddRecipient()}
                  />
                  <Button onClick={handleAddRecipient}>Add</Button>
                </div>
                
                <ScrollArea className="h-[200px]">
                  {config.recipients.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Mail className="h-10 w-10 mx-auto mb-2 opacity-50" />
                      <p>No recipients added yet</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {config.recipients.map((email, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">{email}</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveRecipient(email)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>

                <div className="space-y-2">
                  <Label>Email Subject Prefix</Label>
                  <Input
                    value={config.subject_prefix}
                    onChange={(e) => setConfig(prev => ({ ...prev, subject_prefix: e.target.value }))}
                    placeholder="[CareLens Weekly]"
                  />
                </div>
              </CardContent>
            </ModernCard>
          </div>

          <Button onClick={handleSaveConfig} disabled={saving} className="bg-gradient-to-r from-[#00b0ab] to-[#008a86]">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Save Configuration
          </Button>
        </TabsContent>

        {/* Sections Tab */}
        <TabsContent value="sections">
          <ModernCard>
            <CardHeader>
              <SectionHeader icon={FileText} title="Report Sections" subtitle="Select which sections to include in the weekly report" />
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {REPORT_SECTIONS.map((section) => {
                  const isSelected = config.sections.includes(section.id);
                  return (
                    <div
                      key={section.id}
                      onClick={() => toggleSection(section.id)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        isSelected 
                          ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20' 
                          : 'border-slate-200 dark:border-slate-700 hover:border-violet-300'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${isSelected ? 'bg-violet-500 text-white' : 'bg-slate-100 dark:bg-slate-800'}`}>
                          <section.icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium">{section.name}</h4>
                            <Checkbox checked={isSelected} />
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{section.description}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </ModernCard>
        </TabsContent>

        {/* Preview Tab */}
        <TabsContent value="preview">
          <ModernCard>
            <CardHeader>
              <div className="flex items-center justify-between">
                <SectionHeader icon={Eye} title="Report Preview" subtitle="Preview of the current report configuration" />
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleGenerateNow} disabled={generating}>
                    {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                    Regenerate
                  </Button>
                  <Button variant="outline">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {previewData ? (
                <ScrollArea className="h-[500px]">
                  <div className="space-y-6 p-4 bg-white dark:bg-slate-900 rounded-lg border">
                    {/* Report Header */}
                    <div className="text-center pb-6 border-b">
                      <h1 className="text-2xl font-bold">CareLens Weekly Report</h1>
                      <p className="text-muted-foreground">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    </div>
                    
                    {/* Executive Summary */}
                    {config.sections.includes('executive_summary') && (
                      <div className="space-y-3">
                        <h2 className="text-lg font-semibold flex items-center gap-2">
                          <BarChart3 className="h-5 w-5 text-violet-500" />
                          Executive Summary
                        </h2>
                        <div className="grid grid-cols-4 gap-4">
                          <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                            <p className="text-2xl font-bold">{previewData.summary?.adc || '50.2'}</p>
                            <p className="text-sm text-muted-foreground">ADC</p>
                          </div>
                          <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                            <p className="text-2xl font-bold">{previewData.summary?.quality_score || '94'}%</p>
                            <p className="text-sm text-muted-foreground">Quality Score</p>
                          </div>
                          <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                            <p className="text-2xl font-bold">{previewData.summary?.compliance || '96'}%</p>
                            <p className="text-sm text-muted-foreground">Compliance</p>
                          </div>
                          <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-center">
                            <p className="text-2xl font-bold">{previewData.summary?.active_pips || '3'}</p>
                            <p className="text-sm text-muted-foreground">Active PIPs</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Clinical Quality */}
                    {config.sections.includes('clinical_quality') && (
                      <div className="space-y-3">
                        <h2 className="text-lg font-semibold flex items-center gap-2">
                          <Heart className="h-5 w-5 text-rose-500" />
                          Clinical Quality
                        </h2>
                        <div className="space-y-2">
                          <div className="flex justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                            <span>Pain Control (48hrs)</span>
                            <Badge className="bg-emerald-100 text-emerald-700">92% Met</Badge>
                          </div>
                          <div className="flex justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                            <span>Hospitalization Rate</span>
                            <Badge className="bg-emerald-100 text-emerald-700">3.2%</Badge>
                          </div>
                          <div className="flex justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                            <span>Live Discharge Rate</span>
                            <Badge className="bg-emerald-100 text-emerald-700">2.1%</Badge>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* AI Insights */}
                    {config.include_ai_insights && (
                      <div className="space-y-3">
                        <h2 className="text-lg font-semibold flex items-center gap-2">
                          <Sparkles className="h-5 w-5 text-violet-500" />
                          AI Insights
                        </h2>
                        <div className="p-4 bg-gradient-to-r from-violet-50 to-indigo-50 dark:from-violet-900/20 dark:to-indigo-900/20 rounded-lg">
                          <p className="text-sm">{previewData.ai_insights || 'Overall performance remains strong this week. Key areas of focus should include monitoring the 2 staff members showing elevated burnout risk scores and addressing the upcoming certification renewals.'}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-16">
                  <Eye className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="text-lg font-semibold">No Preview Available</h3>
                  <p className="text-muted-foreground mt-2">Click "Generate Now" to create a preview</p>
                  <Button onClick={handleGenerateNow} className="mt-4" disabled={generating}>
                    {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                    Generate Preview
                  </Button>
                </div>
              )}
            </CardContent>
          </ModernCard>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <ModernCard>
            <CardHeader>
              <SectionHeader icon={History} title="Report History" subtitle="Previously generated and sent reports" />
            </CardHeader>
            <CardContent>
              {reportHistory.length === 0 ? (
                <div className="text-center py-16">
                  <History className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="text-lg font-semibold">No Report History</h3>
                  <p className="text-muted-foreground mt-2">Generated reports will appear here</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {reportHistory.map((report, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${report.status === 'sent' ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-slate-200 dark:bg-slate-700'}`}>
                            {report.status === 'sent' ? (
                              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                            ) : (
                              <FileText className="h-5 w-5 text-slate-500" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{report.title || `Weekly Report - ${report.date}`}</p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(report.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                              {report.recipients && ` • Sent to ${report.recipients.length} recipients`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{report.format?.toUpperCase() || 'PDF'}</Badge>
                          <Button variant="ghost" size="sm">
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </ModernCard>
        </TabsContent>

        {/* Email Preview Tab */}
        <TabsContent value="email-preview">
          <ModernCard>
            <CardHeader>
              <div className="flex items-center justify-between">
                <SectionHeader icon={Mail} title="Email Preview" subtitle="Preview the HTML email that will be sent to recipients" />
                <Button onClick={handlePreviewEmail} variant="outline">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh Preview
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {htmlPreview ? (
                <div className="border rounded-lg overflow-hidden">
                  <iframe 
                    srcDoc={htmlPreview}
                    title="Email Preview"
                    className="w-full h-[600px] bg-white"
                    sandbox=""
                  />
                </div>
              ) : (
                <div className="text-center py-16">
                  <Mail className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="text-lg font-semibold">No Email Preview Available</h3>
                  <p className="text-muted-foreground mt-2">Click "Refresh Preview" to generate the email preview</p>
                  <Button onClick={handlePreviewEmail} className="mt-4">
                    <Eye className="h-4 w-4 mr-2" />
                    Generate Email Preview
                  </Button>
                </div>
              )}
            </CardContent>
          </ModernCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
